/** Browse pages: systems, books, publishers, creators. */
import { Link, useSearchParams } from 'react-router'
import { Plus } from 'lucide-react'
import { BookShelf, SeriesShelf, ShelfSkeleton, SystemShelf } from '../components/Cards'
import { Empty, ErrorState, Pager, usePage } from '../components/Bits'
import { CollectionFilter, SelectFilter, ToggleFilter, VocabFilter } from '../components/Filters'
import { qs } from '../lib/api'
import { categoryLabel, languageName, num, plural, ROLE_LABELS } from '../lib/format'
import { useQuery } from '../lib/query'
import { signInHref, useSession } from '../lib/session'
import { useTitle } from '../lib/useTitle'
import type { BookCard, NamedCard, Page, SeriesCard, Stats, SystemCard, UserProfile } from '../lib/types'
import {
  AVAILABILITY_LABELS,
  FORMAT_LABELS,
  GAME_SIZE_LABELS,
  PLAY_MODE_LABELS,
  SERIES_KIND_LABELS,
  STATUS_LABELS,
} from '../lib/labels'
import {
  AVAILABILITY,
  BOOK_FORMATS,
  CREDIT_ROLES,
  GAME_SIZES,
  PLAY_MODES,
  SERIES_KINDS,
  SYSTEM_STATUSES,
  type CreditRole,
} from '../../shared/model'
import { VerifiedBadge } from '../components/RecordActions'
import { Portrait } from '../components/Portrait'
import { ExplicitNotice } from '../components/ExplicitNotice'
import { RoleIcon } from '../components/RoleIcon'
import { gameParam, useGameScope } from '../lib/gameScope'

export function BrowseHead({
  title,
  total,
  addHref,
  addLabel,
  countLabel,
}: {
  title: string
  total?: number
  addHref: string
  addLabel: string
  countLabel?: (n: number) => string
}) {
  const session = useSession()
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">{title}</h1>
        {total !== undefined && (
          <p className="page-count">{countLabel ? countLabel(total) : `${num(total)} in the database`}</p>
        )}
      </div>
      <Link to={session.user ? addHref : signInHref(addHref)} className="button button-quiet">
        <Plus size={16} aria-hidden="true" /> {addLabel}
      </Link>
    </div>
  )
}

/** "Showing <name>'s collection", when a list is narrowed by someone else's shared collection. */
function CollectorNote() {
  const [params] = useSearchParams()
  const session = useSession()
  const collector = params.get('collector')
  const shown = collector && params.get('owned') && collector !== session.user?.id
  const { data } = useQuery<UserProfile>(shown ? `/api/v1/users/${collector}` : null)
  if (!shown || !data) return null
  return (
    <p className="notice">
      <span>
        Narrowed by <Link to={`/users/${data.id}`}>{data.name}</Link>’s collection.
      </span>
    </p>
  )
}

const SYSTEM_SORTS = [
  { value: 'books', label: 'Most books' },
  { value: 'recent', label: 'Recently changed' },
  { value: 'completeness', label: 'Least complete' },
]

export function SystemsBrowse() {
  useTitle('Systems')
  const [params] = useSearchParams()
  const page = usePage()
  const scope = useGameScope()
  const { wargames } = useSession()
  const key = `/api/v1/systems${qs({
    page,
    game: gameParam(scope),
    game_size: params.get('game_size'),
    genre: params.get('genre'),
    family: params.get('family'),
    status: params.get('status'),
    play_mode: params.get('play_mode'),
    tag: params.get('tag'),
    sort: params.get('sort'),
    incomplete: params.get('incomplete') === '1',
    owned: params.get('owned'),
    collector: params.get('collector'),
    explicit: params.get('explicit'),
    limit: 48,
  })}`
  const { data, error, refresh } = useQuery<Page<SystemCard>>(key)
  const { data: stats } = useQuery<Stats>('/api/v1/stats')
  return (
    <div className="page">
      <BrowseHead
        title="Systems"
        total={data?.total}
        addHref="/systems/new"
        addLabel="Add a system"
        countLabel={(n) =>
          `${num(n)} at the top level${stats && stats.systems > n ? `, ${num(stats.systems)} counting editions and collection members` : ''}`
        }
      />
      <div className="filters">
        <VocabFilter name="genre" kind="genre" label="Genre" allLabel="Any genre" />
        <VocabFilter name="family" kind="system_family" label="Family" allLabel="Any family" />
        {scope !== 'wargame' && (
          <SelectFilter
            name="play_mode"
            label="Way to play"
            options={opts(PLAY_MODES, PLAY_MODE_LABELS)}
            allLabel="Any"
          />
        )}
        {wargames && scope !== 'ttrpg' && (
          <SelectFilter
            name="game_size"
            label="Game size"
            options={opts(GAME_SIZES, GAME_SIZE_LABELS)}
            allLabel="Any size"
          />
        )}
        <SelectFilter
          name="status"
          label="Status"
          options={opts(SYSTEM_STATUSES.filter(Boolean), STATUS_LABELS)}
          allLabel="Any status"
        />
        <CollectionFilter />
        <SelectFilter name="sort" label="Sort" options={SYSTEM_SORTS} allLabel="Name" />
        <ToggleFilter name="incomplete" label="Only records with gaps" />
      </div>
      <CollectorNote />
      <ExplicitNotice explicit={data?.explicit} noun="system" />
      {error ? (
        <ErrorState error={error} retry={refresh} />
      ) : !data ? (
        <ShelfSkeleton />
      ) : data.items.length === 0 ? (
        <Empty title="No systems match">Try a different genre or family, or clear the filters.</Empty>
      ) : (
        <>
          <SystemShelf items={data.items} label="Systems" />
          <Pager page={data.page} limit={data.limit} total={data.total} />
        </>
      )}
    </div>
  )
}

const BOOK_SORTS = [
  { value: 'recent', label: 'Recently changed' },
  { value: 'year', label: 'Newest first' },
  { value: 'completeness', label: 'Least complete' },
]

export function BooksBrowse() {
  useTitle('Books')
  const [params] = useSearchParams()
  const page = usePage()
  const scope = useGameScope()
  const key = `/api/v1/books${qs({
    page,
    game: gameParam(scope),
    category: params.get('category'),
    language: params.get('language'),
    genre: params.get('genre'),
    tag: params.get('tag'),
    availability: params.get('availability'),
    format: params.get('format'),
    level: params.get('level'),
    sort: params.get('sort'),
    incomplete: params.get('incomplete') === '1',
    owned: params.get('owned'),
    collector: params.get('collector'),
    explicit: params.get('explicit'),
    limit: 48,
  })}`
  const { data, error, refresh } = useQuery<Page<BookCard>>(key)
  return (
    <div className="page">
      <BrowseHead title="Books" total={data?.total} addHref="/books/new" addLabel="Add a book" />
      <div className="filters">
        <VocabFilter name="category" kind="category" label="Category" allLabel="Any category" format={categoryLabel} />
        <VocabFilter name="language" kind="language" label="Language" allLabel="Any language" format={languageName} />
        <SelectFilter
          name="availability"
          label="Availability"
          options={opts(AVAILABILITY.filter(Boolean), AVAILABILITY_LABELS)}
          allLabel="Any"
        />
        <SelectFilter name="format" label="Format" options={opts(BOOK_FORMATS, FORMAT_LABELS)} allLabel="Any format" />
        <CollectionFilter />
        <SelectFilter name="sort" label="Sort" options={BOOK_SORTS} allLabel="Title" />
        <ToggleFilter name="incomplete" label="Only records with gaps" />
      </div>
      <CollectorNote />
      <ExplicitNotice explicit={data?.explicit} noun="book" />
      {error ? (
        <ErrorState error={error} retry={refresh} />
      ) : !data ? (
        <ShelfSkeleton />
      ) : data.items.length === 0 ? (
        <Empty title="No books match">Clear a filter to see more.</Empty>
      ) : (
        <>
          <BookShelf items={data.items} label="Books" />
          <Pager page={data.page} limit={data.limit} total={data.total} />
        </>
      )}
    </div>
  )
}

/** Publishers and creators: a dense list, since they have no covers. */
export function NamedBrowse({ type }: { type: 'publisher' | 'creator' }) {
  const title = type === 'publisher' ? 'Publishers' : 'Creators'
  useTitle(title)
  const [params] = useSearchParams()
  const page = usePage()
  const path = type === 'publisher' ? 'publishers' : 'creators'
  const creators = type === 'creator'
  const { data, error, refresh } = useQuery<Page<NamedCard>>(
    `/api/v1/${path}${qs({ page, sort: params.get('sort'), role: creators ? params.get('role') : null, limit: 48 })}`,
  )
  return (
    <div className="page">
      <BrowseHead
        title={title}
        total={data?.total}
        addHref={`/${path}/new`}
        addLabel={type === 'publisher' ? 'Add a publisher' : 'Add a creator'}
      />
      <div className="filters">
        {creators && (
          <SelectFilter
            name="role"
            label="Role"
            options={CREDIT_ROLES.map((r) => ({ value: r, label: `${ROLE_LABELS[r]}s` }))}
            allLabel="Any role"
          />
        )}
        <SelectFilter
          name="sort"
          label="Sort"
          options={[
            { value: 'books', label: 'Most books' },
            { value: 'recent', label: 'Recently changed' },
          ]}
          allLabel="Name"
        />
      </div>
      {error ? (
        <ErrorState error={error} retry={refresh} />
      ) : !data ? null : data.items.length === 0 ? (
        <Empty title={`No ${title.toLowerCase()} match`}>
          {params.get('role') ? 'Try another role.' : 'They are added automatically when a book or system names them.'}
        </Empty>
      ) : (
        <>
          <ul className={creators ? 'name-grid is-people' : 'name-grid'}>
            {data.items.map((r) => (
              <li key={r.id}>
                <Link to={`/${path}/${r.id}`} className="name-card">
                  {creators && <Portrait photo={r.photo} name={r.name} />}
                  <span className="name-card-body">
                    <span className="name-card-name">
                      {r.name} {r.verified && <VerifiedBadge />}
                    </span>
                    {creators && r.roles.length > 0 && (
                      <span className="role-list">
                        {r.roles.slice(0, 3).map((x) => (
                          <span key={x.role} className="role-chip">
                            <RoleIcon role={x.role} size={13} />
                            {ROLE_LABELS[x.role as CreditRole] ?? x.role}
                          </span>
                        ))}
                      </span>
                    )}
                    <span className="name-card-meta">
                      {[r.system_count ? plural(r.system_count, 'system') : '', plural(r.book_count, 'book')]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <span className="name-card-gauge" aria-label={`${r.completeness}% complete`}>
                    <span style={{ width: `${r.completeness}%` }} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <Pager page={data.page} limit={data.limit} total={data.total} />
        </>
      )}
    </div>
  )
}

export const opts = <T extends string>(values: readonly T[], labels: Record<T, string>) =>
  values.map((v) => ({ value: v, label: labels[v] }))

export function SeriesBrowse() {
  useTitle('Series & settings')
  const [params] = useSearchParams()
  const page = usePage()
  const scope = useGameScope()
  const key = `/api/v1/series${qs({
    page,
    game: gameParam(scope),
    kind: params.get('kind'),
    system: params.get('system'),
    sort: params.get('sort'),
    explicit: params.get('explicit'),
    limit: 48,
  })}`
  const { data, error, refresh } = useQuery<Page<SeriesCard>>(key)
  return (
    <div className="page">
      <BrowseHead title="Series & settings" total={data?.total} addHref="/series/new" addLabel="Add a series" />
      <p className="lede">Campaign settings, product lines, adventure series and collections that books belong to.</p>
      <div className="filters">
        <SelectFilter name="kind" label="Kind" options={opts(SERIES_KINDS, SERIES_KIND_LABELS)} allLabel="Any kind" />
        <SelectFilter
          name="sort"
          label="Sort"
          options={[
            { value: 'books', label: 'Most books' },
            { value: 'recent', label: 'Recently changed' },
          ]}
          allLabel="Name"
        />
      </div>
      <ExplicitNotice explicit={data?.explicit} noun="series" nouns="series" />
      {error ? (
        <ErrorState error={error} retry={refresh} />
      ) : !data ? (
        <ShelfSkeleton />
      ) : data.items.length === 0 ? (
        <Empty title="No series yet">Add one, or name a series on a book and it is created for you.</Empty>
      ) : (
        <>
          <SeriesShelf items={data.items} label="Series" />
          <Pager page={data.page} limit={data.limit} total={data.total} />
        </>
      )}
    </div>
  )
}
